import * as React from "react";
import { AddressDisplayProvider, useLoadBudget } from "@authe/ui-core";
import type { FocusTarget } from "@authe/fe-commons/focus";
import { overlayRefKey, type OverlayRef } from "@authe/fe-commons/overlays";
import { useCatalogApi } from "@/api/adapters";
import { useSubjectHrefs } from "@/features/inspector/useSubjectHrefs";
import { useSubjectOpener } from "@/features/inspector/useSubjectOpener";
import { useResolvedAddressOverlaysBatch } from "./useResolvedAddressOverlaysBatch";

/** Shared address defaults for routed views, including panels rendered in portals. */
export function DashboardAddressDisplayProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const { addressHref } = useSubjectHrefs();
  const { openAddress } = useSubjectOpener();
  const catalog = useCatalogApi();
  const budget = useLoadBudget("catalog.enrichment");
  const mounted = React.useRef(new Map<string, { ref: OverlayRef; count: number }>());
  const [refs, setRefs] = React.useState<OverlayRef[]>([]);
  const queued = React.useRef(false);
  const live = React.useRef(true);
  React.useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
  const flush = React.useCallback(() => {
    if (queued.current) return;
    queued.current = true;
    queueMicrotask(() => {
      queued.current = false;
      if (!live.current) return;
      const next = [...mounted.current.values()].map(entry => entry.ref);
      setRefs(previous => previous.length === next.length && previous.every((ref, i) =>
        overlayRefKey(ref.chainId, ref.address) === overlayRefKey(next[i]!.chainId, next[i]!.address)) ? previous : next);
    });
  }, []);
  const registerAddress = React.useCallback((address: string, chainId?: string) => {
    const key = overlayRefKey(chainId, address);
    const current = mounted.current.get(key);
    if (current) current.count += 1;
    else mounted.current.set(key, { ref: { address, chainId: chainId ?? null }, count: 1 });
    flush();
    return () => {
      const entry = mounted.current.get(key);
      if (entry && --entry.count === 0) mounted.current.delete(key);
      flush();
    };
  }, [flush]);
  // Reuse the identity/scoped per-address cache and coalesced transport. This
  // also shares answers with tables and graphs that already requested a ref.
  const overlays = useResolvedAddressOverlaysBatch(refs, {
    includeRow: true,
    maxRefs: budget.enrichment,
    resolveLogoUrl: catalog.resolveLogoUrl,
  });
  const onDrilldown = React.useCallback((target: FocusTarget) => {
    if (target.kind !== "address") return;
    openAddress(target.id, target.chain || undefined);
  }, [openAddress]);
  return <AddressDisplayProvider presentation="chip" getInternalHref={addressHref}
    onDrilldown={onDrilldown} resolveOverlay={overlays.resolveOverlay} registerAddress={registerAddress}>
    {children}
  </AddressDisplayProvider>;
}
